import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import './FAQ.css';
import FAQHero from './FAQHero';
import Newsletter from '../Homepage/Newsletter';
import Footer from '../Layouts/Footer';
import Canonical from '../../seo/Canonical';
import RelatedLinks from '../Common/RelatedLinks';
import {
  FULFILLMENT_FAQ_CATEGORIES as categories,
  FULFILLMENT_FAQ_ITEMS as faqs,
} from '../../data/fulfillmentFaqPageData';
import { FAQ_RELATED_BLOGS } from '../../data/resourcePageRelatedBlogs';

const FAQ = () => {
  const [activeCategory, setActiveCategory] = useState('general');
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const filteredFaqs = faqs.filter((f) => f.category === activeCategory);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const handleCategoryChange = (id: string) => {
    setActiveCategory(id);
    setOpenIndex(null);
  };

  return (
    <div className="faq-page logistics-page">
      <Helmet>
        <meta
          name="keywords"
          content="fulfilment FAQs, e-commerce fulfilment questions, Amazon FBA FAQ, SFP questions, fulfilment services UK, pick and pack FAQ"
        />
        <meta httpEquiv="X-Robots-Tag" content="index, follow" />
        <meta name="publisher" content="Pick Pack Pro" />
      </Helmet>
      <Canonical baseUrl="https://www.pickpackpro.co.uk" />

      <FAQHero />

      <section className="faq-content-section">
        <div className="faq-content-container">
          <div className="faq-grid">
            <div className="faq-sidebar">
              <div className="faq-sidebar-card">
                <h2 className="faq-sidebar-title">Categories</h2>
                <ul className="faq-sidebar-list">
                  {categories.map((cat) => (
                    <li key={cat.id}>
                      <button
                        type="button"
                        className={`faq-cat-btn ${activeCategory === cat.id ? 'faq-cat-btn--active' : ''}`}
                        onClick={() => handleCategoryChange(cat.id)}
                      >
                        <span className="faq-cat-dot" />
                        {cat.label}
                        <span className="faq-cat-count">
                          {faqs.filter((f) => f.category === cat.id).length}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="faq-sidebar-card faq-sidebar-cta">
                <h2 className="faq-sidebar-title">Still Have Questions?</h2>
                <p className="faq-sidebar-text">
                  Our team is here to help. Get in touch for personalised assistance — or browse guides like{' '}
                  <Link to="/blog/how-to-choose-fulfillment-partner">
                    how to choose a fulfilment partner
                  </Link>
                  ,{' '}
                  <Link to="/blog/what-is-fba-prep">what is FBA prep</Link>, and{' '}
                  <Link to="/blog/ecommerce-fulfilment-guide-uk">ecommerce fulfilment UK</Link>.
                </p>
                <a href="/contact-fulfillment-services" className="faq-cta-button">
                  Contact us for a UK fulfilment quote
                </a>
              </div>
            </div>

            <div className="faq-main-content">
              <div className="faq-category-heading">
                {categories.find((c) => c.id === activeCategory)?.label}
              </div>
              <div className="faq-list">
                {filteredFaqs.map((faq, index) => (
                  <div key={faq.id} className={`faq-item ${openIndex === index ? 'faq-open' : ''}`}>
                    <div className="faq-question-wrapper" onClick={() => toggleFAQ(index)}>
                      <div className="faq-number">{String(index + 1).padStart(2, '0')}</div>
                      <h2 className="faq-question">{faq.question}</h2>
                      <button
                        type="button"
                        className={`faq-toggle ${openIndex === index ? 'faq-toggle-open' : ''}`}
                        aria-label={openIndex === index ? 'Close answer' : 'Open answer'}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFAQ(index);
                        }}
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          {openIndex === index ? (
                            <path d="M5 12H19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                          ) : (
                            <>
                              <path d="M12 5V19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                              <path d="M5 12H19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                            </>
                          )}
                        </svg>
                      </button>
                    </div>
                    <div className={`faq-answer ${openIndex === index ? 'faq-answer-open' : ''}`}>
                      <p>{faq.answer}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <RelatedLinks
        title="Related fulfilment guides"
        serviceLinks={FAQ_RELATED_BLOGS}
        blogLink={{ to: '/fulfillment-blog', label: 'All fulfilment blog articles' }}
      />

      <Newsletter />
      <Footer />
    </div>
  );
};

export default FAQ;
